import React, { useState, useEffect } from 'react';
import { Settings, X, Upload, Download, Layout, RotateCcw, Zap, BrainCircuit, ChevronsUpDown, ChevronsDownUp, Moon, Sun, Undo2, Globe, FilePlus, Smile, CircleDot, Image as ImageIcon, AlertCircle, Cloud, CloudCheck, CloudOff, RefreshCw, Pencil, Check, Play } from 'lucide-react';

import { SidebarProps } from '../types';
import { PALETTES } from '../constants';
import { translations } from '../utils/translations';

const Sidebar: React.FC<SidebarProps> = ({
  userName,
  fileName,
  syncStatus,
  currentMindmapId,
  onFileUpload,

  onCreateNew,
  onSave,
  onExportImage,
  onRearrange,
  onReset,
  onExpandAll,
  onCollapseLeaves,
  onUndo,
  onReplay,
  canUndo,
  isReplaying,
  currentPalette,
  onPaletteChange,
  selectedNode,
  isDarkMode,
  onToggleDarkMode,
  language,
  onToggleLanguage,
  showEmoji,
  onToggleEmoji,
  isDirty,
  isMandalaMode,
  onToggleMandalaMode,
  onOpenCloudModal,
  onRenameProject,
  onImageUpload,
  isImportingImage,
  onXMindUpload,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const t = translations[language];

  const handleStartRename = () => {
    setTempTitle(fileName.replace(/\.(md|txt)$/i, ''));
    setIsEditingTitle(true);
  };

  const handleFinishRename = () => {
    const clean = tempTitle.trim();
    if (clean && onRenameProject) {
      onRenameProject(clean);
    }
    setIsEditingTitle(false);
  };

  const lastUpdated = "2026-09-09 12:09 JST";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      onFileUpload(text, file.name);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const filteredPalettes = PALETTES.filter(p => p.type === (isDarkMode ? 'dark' : 'light'));

  if (!isOpen) {
      return (
          <button 
            onClick={() => setIsOpen(true)}
            className="absolute top-4 left-4 z-50 p-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-200 rounded-lg shadow-lg hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"
          >
              <Settings className="w-6 h-6" />
          </button>
      )
  }

  return (
    <div className="absolute top-0 left-0 h-full w-80 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-xl border-r border-slate-200 dark:border-slate-800 flex flex-col z-40 transition-all duration-300">
      
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-start bg-white dark:bg-slate-900">
        <div className="flex flex-col gap-1 overflow-hidden">
            <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-blue-400 dark:to-indigo-400 flex items-center gap-2">
                <BrainCircuit className="w-6 h-6 text-indigo-500 dark:text-indigo-400 shrink-0" />
                {t.appName}
            </h1>
            <div className="flex items-center gap-2 pl-1">
              {isEditingTitle ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFinishRename();
                      if (e.key === 'Escape') setIsEditingTitle(false);
                    }}
                    autoFocus
                    className="text-[11px] px-1.5 py-0.5 border border-indigo-400 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-mono focus:outline-none w-[130px]"
                  />
                  <button onClick={handleFinishRename} className="p-0.5 text-green-500 hover:text-green-600">
                    <Check className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  className="group flex items-center gap-1 cursor-pointer text-[10px] text-slate-500 dark:text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 font-mono max-w-[150px] truncate"
                  onClick={handleStartRename}
                  title="Click to rename project"
                >
                  <span className="truncate">{fileName}</span>
                  <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>
              )}
              {isDirty && (
                <div className="flex items-center gap-1 text-[10px] font-bold text-orange-500 dark:text-orange-400 animate-pulse whitespace-nowrap">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400" />
                  UNSAVED
                </div>
              )}
              
              <div className="flex items-center gap-1.5 ml-1">
                {syncStatus === 'saving' && (
                  <div className="flex items-center gap-1 text-[9px] text-indigo-500 dark:text-indigo-400 font-medium">
                    <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                    SAVING...
                  </div>
                )}
                {syncStatus === 'synced' && (
                  <div className="flex items-center gap-1 text-[9px] text-blue-500 dark:text-blue-400 font-medium" title="Synced with Cloud">
                    <Cloud className="w-2.5 h-2.5" />
                    SYNCED
                  </div>
                )}
                {syncStatus === 'unsaved' && (
                  <div className="flex items-center gap-1 text-[9px] text-amber-500 dark:text-amber-400 font-medium" title="Unsaved Changes">
                    <CloudOff className="w-2.5 h-2.5" />
                    UNSAVED
                  </div>
                )}
                {syncStatus === 'error' && (
                  <div className="flex items-center gap-1 text-[9px] text-red-500 dark:text-red-400 font-medium" title="Sync Error">
                    <AlertCircle className="w-2.5 h-2.5" />
                    ERROR
                  </div>
                )}
                {syncStatus === 'local' && (
                  <div className="flex items-center gap-1 text-[9px] text-slate-400 dark:text-slate-500 font-medium" title="Local Draft (Not Synced)">
                    <CloudOff className="w-2.5 h-2.5" />
                    LOCAL
                  </div>
                )}
              </div>
            </div>

        </div>
        <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shrink-0">
            <X className="w-5 h-5"/>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent">
        
        {/* Toggles */}
        <section className="space-y-2">
             <button 
                onClick={onToggleDarkMode}
                title={t.darkModeTooltip}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors"
             >
                 <div className="flex items-center gap-3">
                     <div className={`p-1.5 rounded-full ${isDarkMode ? 'bg-indigo-900/50 text-indigo-400' : 'bg-amber-100 text-amber-500'}`}>
                         {isDarkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                     </div>
                     <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                         {isDarkMode ? t.darkMode : t.lightMode}
                     </span>
                 </div>
                 
                 <div className={`w-10 h-5 rounded-full relative transition-colors ${isDarkMode ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                     <div className={`absolute top-1 left-1 w-3 h-3 rounded-full bg-white transition-transform ${isDarkMode ? 'translate-x-5' : 'translate-x-0'}`} />
                 </div>
             </button>
             
             <button 
                onClick={onToggleMandalaMode}
                title={t.mandalaModeTooltip}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors"
             >
                 <div className="flex items-center gap-3">
                     <div className="p-1.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                         <CircleDot className="w-4 h-4" />
                     </div>
                     <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                         {t.mandalaMode}
                     </span>
                 </div>
                 
                 <div className={`w-10 h-5 rounded-full relative transition-colors ${isMandalaMode ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                     <div className={`absolute top-1 left-1 w-3 h-3 rounded-full bg-white transition-transform ${isMandalaMode ? 'translate-x-5' : 'translate-x-0'}`} />
                 </div>
             </button>

             <button 
                onClick={onToggleLanguage}
                title={t.languageTooltip}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors"
             >
                 <div className="flex items-center gap-3">
                     <div className="p-1.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                         <Globe className="w-4 h-4" />
                     </div>
                     <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                         {t.language}
                     </span>
                 </div>
                 
                 <span className="text-xs font-bold bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded text-slate-600 dark:text-slate-300">
                     {language === 'en' ? 'EN' : 'JP'}
                 </span>
             </button>

             <button 
                onClick={onToggleEmoji}
                title={t.emojiTooltip}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors"
             >
                 <div className="flex items-center gap-3">
                     <div className="p-1.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                         <Smile className="w-4 h-4" />
                     </div>
                     <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                         {t.showEmoji}
                     </span>
                 </div>
                 
                 <div className={`w-10 h-5 rounded-full relative transition-colors ${showEmoji ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                     <div className={`absolute top-1 left-1 w-3 h-3 rounded-full bg-white transition-transform ${showEmoji ? 'translate-x-5' : 'translate-x-0'}`} />
                 </div>
             </button>
        </section>

        {/* Actions Section */}
        <section className="space-y-3">
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t.fileOperations}</h2>
          
          <button 
            onClick={onOpenCloudModal}
            title="Cloud Storage"
            className="flex items-center justify-center w-full px-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
          >
             <Cloud className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
             <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">
               {userName ? `Cloud (${userName})` : "Cloud Storage"}
             </span>
          </button>

          <button 
            onClick={onCreateNew}
            title={t.createNewTooltip}
            className="flex items-center justify-center w-full px-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
          >
             <FilePlus className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
             <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">{t.createNew}</span>
          </button>

          <label 
            title={t.openMarkdownTooltip}
            className="flex items-center justify-center w-full px-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
          >
            <Upload className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
            <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">{t.openMarkdown}</span>
            <input type="file" accept=".md,.txt" className="hidden" onChange={handleFileChange} />
          </label>

          <label 
            title="Import Mindmap from Image"
            className={`flex items-center justify-center w-full px-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg transition-colors shadow-sm gap-2 group ${isImportingImage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700'}`}
          >
            {isImportingImage ? (
               <RefreshCw className="w-4 h-4 text-indigo-500 animate-spin" />
            ) : (
               <ImageIcon className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-400" />
            )}
            <span className={`text-sm ${isImportingImage ? 'text-indigo-500' : 'text-slate-600 dark:text-slate-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400'}`}>
              {isImportingImage ? 'Importing...' : 'Import Image'}
            </span>
            <input 
              type="file" 
              accept="image/*" 
              className="hidden" 
              disabled={isImportingImage}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onImageUpload) {
                  onImageUpload(file);
                }
                e.target.value = '';
              }} 
            />
          </label>

          <label 
            title="Import XMind File"
            className="flex items-center justify-center w-full px-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
          >
            <Upload className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-amber-500 dark:group-hover:text-amber-400" />
            <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-amber-600 dark:group-hover:text-amber-400">Import XMind</span>
            <input 
              type="file" 
              accept=".xmind" 
              className="hidden" 
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onXMindUpload) {
                  onXMindUpload(file);
                }
                e.target.value = '';
              }} 
            />
          </label>



          <button 
            onClick={onSave}
            title={t.saveTooltip}
            className={`flex items-center justify-center w-full px-4 py-2 border rounded-lg transition-all shadow-sm gap-2 group ${isDirty ? 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
          >
             <Download className={`w-4 h-4 ${isDirty ? 'text-white' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'}`} />
             <span className={`text-sm font-medium ${isDirty ? 'text-white' : 'text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'}`}>{t.saveMindMap}</span>
          </button>

          <button 
            onClick={onExportImage}
            title="Export as WebP Image"
            className="flex items-center justify-center w-full px-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
          >
             <ImageIcon className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
             <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">Export Image</span>
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button 
                onClick={onExpandAll}
                className="flex items-center justify-center px-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
                title={t.expandAllTooltip}
            >
                <ChevronsUpDown className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">{t.expandAll}</span>
            </button>

            <button 
                onClick={onCollapseLeaves}
                className="flex items-center justify-center px-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
                title={t.collapseTooltip}
            >
                <ChevronsDownUp className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">{t.collapse}</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button 
                onClick={onRearrange}
                className="flex items-center justify-center px-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm gap-2 group"
                title={t.rearrangeTooltip}
            >
                <Layout className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">{t.rearrange}</span>
            </button>

            <button 
                onClick={onUndo}
                disabled={!canUndo}
                className={`flex items-center justify-center px-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg transition-colors shadow-sm gap-2 group ${!canUndo ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                title={t.undoTooltip}
            >
                <Undo2 className={`w-4 h-4 text-slate-500 dark:text-slate-400 ${canUndo ? 'group-hover:text-slate-700 dark:group-hover:text-slate-200' : ''}`} />
                <span className={`text-sm text-slate-600 dark:text-slate-300 ${canUndo ? 'group-hover:text-slate-900 dark:group-hover:text-white' : ''}`}>{t.undo}</span>
            </button>
          </div>

          <button 
                onClick={onReset}
                disabled={isReplaying}
                className={`w-full flex items-center justify-center px-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg transition-colors shadow-sm gap-2 group ${isReplaying ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50 hover:border-red-200 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:border-red-800 dark:hover:text-red-400'}`}
                title={t.resetTooltip}
            >
                <RotateCcw className={`w-4 h-4 text-slate-500 dark:text-slate-400 ${!isReplaying ? 'group-hover:text-red-600 dark:group-hover:text-red-400' : ''}`} />
                <span className={`text-sm text-slate-600 dark:text-slate-300 ${!isReplaying ? 'group-hover:text-red-600 dark:group-hover:text-red-400' : ''}`}>{t.reset}</span>
          </button>
          
          <button 
                onClick={onReplay}
                disabled={!canUndo || isReplaying}
                className={`w-full flex items-center justify-center px-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg transition-colors shadow-sm gap-2 group ${(!canUndo || isReplaying) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600 dark:hover:bg-indigo-900/30 dark:hover:border-indigo-800 dark:hover:text-indigo-400'}`}
                title={t.replayTooltip}
            >
                {isReplaying ? (
                    <RefreshCw className="w-4 h-4 text-indigo-500 animate-spin" />
                ) : (
                    <Play className={`w-4 h-4 text-slate-500 dark:text-slate-400 ${canUndo ? 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400' : ''}`} />
                )}
                <span className={`text-sm ${isReplaying ? 'text-indigo-500 font-medium' : 'text-slate-600 dark:text-slate-300'} ${canUndo && !isReplaying ? 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400' : ''}`}>
                    {isReplaying ? t.replaying : t.replay}
                </span>
          </button>
        </section>

        {/* Themes Section - Compact Grid */}
        <section>
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t.theme}</h2>
          <div className="grid grid-cols-2 gap-2">
            {filteredPalettes.map((p) => (
              <button
                key={p.name}
                onClick={() => onPaletteChange(p)}
                title={p.name === 'Dynamic' ? t.dynamicTip : undefined}
                className={`flex flex-col items-start p-2 rounded-lg border text-xs transition-all
                    ${currentPalette.name === p.name 
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 ring-1 ring-indigo-500/50' 
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
              >
                <div className="flex gap-1 mb-1.5">
                    {p.colors.slice(0, 3).map((c, i) => (
                        <div 
                            key={i} 
                            className="w-2.5 h-2.5 rounded-full" 
                            style={{ background: c }}
                        />
                    ))}
                </div>
                <span className={`font-medium ${currentPalette.name === p.name ? 'text-indigo-600 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-300'}`}>
                    {p.name}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* NOTE: Gemini / AI integration has been removed. The user explicitly requested not to use Gemini in this app. */}
      </div>

      <div className="p-3 border-t border-slate-200 dark:border-slate-800 text-[10px] text-slate-500 text-center bg-white dark:bg-slate-900">
        <div>{t.tabTip}</div>
        <div className="mt-1 text-slate-600 dark:text-slate-400">{t.updated}: {lastUpdated}</div>
      </div>
    </div>
  );
};

export default Sidebar;